import { Box, Typography, Container, Paper } from "@mui/material";
import { useEffect, useMemo } from "react";
import { generateClient } from "aws-amplify/api";
import { listUserLessons } from "../../graphql/queries";
import { useUserContext } from "../../context/UserContext";
import { useLessonContext } from "../../context/LessonContext";
import DummyText from "../dummyText/DummyText";
import bloodCellsBackground from "../../assets/globulos_rojos_2x.png";
import dpLogo from "../../assets/dp_logo.png";

function MainPage() {
  const { state: userState } = useUserContext();
  const { state: lessonState, dispatch: lessonDispatch } = useLessonContext();
  const { userAttributes } = userState;
  const { hasLoadedUserLessons } = lessonState;
  const client = useMemo(() => generateClient(), []);

  // Load user lessons when component mounts if not already loaded
  useEffect(() => {
    const fetchUserLessons = async () => {
      if (!userAttributes) return;
      
      try {
        const result = await client.graphql({
          query: listUserLessons,
          variables: { filter: { userID: { eq: userAttributes.sub } } },
        });
        const userLessonsData = result.data.listUserLessons.items;
        lessonDispatch({ type: 'SET_USER_LESSONS', payload: userLessonsData });
      } catch (error) {
        console.error("Error fetching user lessons:", error);
      }
    };

    if (userAttributes && !hasLoadedUserLessons) {
      fetchUserLessons();
    }
  }, [userAttributes, hasLoadedUserLessons, client, lessonDispatch]);

  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        width: '100vw',
        backgroundImage: `url(${bloodCellsBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        margin: 0,
        position: 'relative',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      })}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={8}
          sx={{
            padding: { xs: 4, sm: 5, md: 6, lg: 7 },
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(15px)',
            borderRadius: 5,
            textAlign: 'center',
            border: '2px solid rgba(128, 0, 32, 0.2)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
            width: '90%',
            maxWidth: '900px',
          }}
        >
          <Box
            component="img"
            src={dpLogo}
            alt="Drepanocítico Logo"
            sx={{
              width: { xs: '120px', sm: '150px', md: '180px' },
              height: 'auto',
              marginBottom: 3,
              filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.2))',
            }}
          />
          
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              color: '#800020',
              fontWeight: 'bold',
              marginBottom: 2,
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            Bienvenido a Drepanocítico
          </Typography>
          
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              color: '#8B4513',
              fontWeight: 500,
              marginBottom: 3,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
            }}
          >
            Educación Virtual para la Comunidad Médica
          </Typography>
          
          <Typography
            variant="body1"
            paragraph
            sx={{
              color: '#333',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              lineHeight: 1.7,
              marginBottom: 3,
            }}
          >
            Drepanocítico es una plataforma de educación virtual dedicada a informar, concientizar y apoyar a pacientes, familiares y profesionales de la salud sobre la drepanocitosis (anemia de células falciformes).
          </Typography>
          
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{
              color: '#800020',
              fontWeight: 600,
              marginBottom: 2,
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
            }}
          >
            Conoce, aprende y comparte sobre la drepanocitosis.
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.6,
              fontStyle: 'italic',
            }}
          >
            Este es un proyecto implementado con el apoyo experto y financiero del 
            St. Jude Children Research Hospital y la Asociación de caridad Americana - Libanesa - Siria (ALSAC)
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}

export default MainPage;
